import { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Icon, type IconName } from "@/lib/icons";
import { cn } from "@/lib/utils";
import { HistoryFilters } from "./HistoryFilters";
import { fill, type MyContributionsCopy } from "./my-contributions-copy";
import { Skeleton } from "@/components/ui/skeleton";
import {
  EMPTY_FILTERS,
  type HistoryFilters as Filters,
  type HistoryKind,
  type HistoryStatus,
} from "./contribution-history";
import { HISTORY_PAGE_SIZE } from "./history-query";
import { useContributionHistory } from "./use-contribution-history";

interface ContributionHistoryProps {
  copy: MyContributionsCopy;
  userId: string | undefined;
  /** Languages the contributor worked in, for the language filter. */
  languages: string[];
  languageName: (code: string | null) => string;
  locale: string;
  format: (value: number) => string;
  /** "{start}-{end} sur {total}", from translations.ts. */
  rangeLabel: string;
  playLabel: string;
  playingBlob: string | null;
  onPlay: (blobName: string) => void;
}

const KIND_ICON: Record<HistoryKind, IconName> = {
  audio: "mic",
  text: "fileText",
  phrase_recording: "mic",
  phrase_created: "languages",
  other: "fileText",
};

const STATUS_CLASS: Record<HistoryStatus, string> = {
  validated: "bg-success/10 text-success",
  pending: "bg-warning/10 text-warning",
  rejected: "bg-danger/10 text-danger",
};

/**
 * Everything the contributor sent, newest first. Status, language, dates and
 * paging are done by the server, so the counts and pages are exact.
 */
export function ContributionHistory(props: ContributionHistoryProps) {
  const { copy, languages, languageName, locale, format, rangeLabel } = props;
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const history = useContributionHistory(props.userId, filters, page);
  const current = {
    items: history.entries,
    total: history.total,
    page,
    pageCount: history.pageCount,
    start: history.total === 0 ? 0 : (page - 1) * HISTORY_PAGE_SIZE + 1,
    end: Math.min(page * HISTORY_PAGE_SIZE, history.total),
  };
  const counts = history.counts;

  const dateFormat = useMemo(() => new Intl.DateTimeFormat(locale, { dateStyle: "medium", timeStyle: "short" }), [locale]);
  const kindLabel: Record<HistoryKind, string> = {
    audio: copy.kindAudio,
    text: copy.kindText,
    phrase_recording: copy.kindPhraseRecording,
    phrase_created: copy.kindPhraseCreated,
    other: copy.kindOther,
  };
  const statusLabel: Record<HistoryStatus, string> = {
    validated: copy.badgeValidated,
    pending: copy.badgePending,
    rejected: copy.badgeRejected,
  };

  const change = (next: Filters) => {
    setFilters(next);
    setPage(1);
  };

  return (
    <section aria-labelledby="mc-history" className="space-y-4">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="mc-history" className="text-sm font-medium text-muted-foreground">
          {copy.historyTitle}
        </h2>
        <span className="text-xs tabular-nums text-faint-foreground">{fill(copy.historyCount, { count: format(counts.all) })}</span>
      </div>

      <HistoryFilters
        copy={copy}
        filters={filters}
        counts={counts}
        languages={languages}
        languageName={languageName}
        format={format}
        onChange={change}
        onClear={() => change(EMPTY_FILTERS)}
      />

      <div className={cn("overflow-hidden rounded-lg border border-border/60 bg-surface", history.isFetching && !history.isLoading && "opacity-70")} aria-busy={history.isFetching}>
        {history.isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 bg-muted/40" />
            ))}
          </div>
        ) : history.isError ? (
          <EmptyState
            compact
            icon="alertTriangle"
            title={copy.loadError}
            action={
              <Button size="sm" variant="outline" onClick={() => void history.refetch()}>
                {copy.retry}
              </Button>
            }
          />
        ) : current.total === 0 ? (
          <EmptyState
            compact
            icon="inbox"
            title={copy.noMatchTitle}
            description={copy.noMatchHint}
            action={
              <Button size="sm" variant="outline" onClick={() => change(EMPTY_FILTERS)}>
                {copy.clearFilters}
              </Button>
            }
          />
        ) : (
          <>
            <div className="hidden grid-cols-[minmax(0,1.4fr)_minmax(0,1.3fr)_minmax(0,1fr)_2.5rem] gap-4 border-b border-border/60 bg-surface-2 px-4 py-2.5 text-xs font-medium text-muted-foreground sm:grid" aria-hidden="true">
              <span>{copy.colType}</span>
              <span>{copy.colDate}</span>
              <span>{copy.colStatus}</span>
              <span />
            </div>
            <ul className="divide-y divide-border/60">
              {current.items.map((entry) => {
                const playing = props.playingBlob !== null && props.playingBlob === entry.blobName;
                return (
                  <li
                    key={entry.key}
                    className="flex items-center gap-4 px-4 py-3 sm:grid sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1.3fr)_minmax(0,1fr)_2.5rem]"
                  >
                    {/* One column on a phone; the wrappers dissolve into the grid from sm. */}
                    <div className="min-w-0 flex-1 space-y-1 sm:contents sm:space-y-0">
                      <span className="flex min-w-0 items-center gap-2.5">
                        <span className="grid size-7 shrink-0 place-items-center rounded-md bg-brand/10 text-brand" aria-hidden="true">
                          <Icon name={KIND_ICON[entry.kind]} className="size-3.5" />
                        </span>
                        <span className="min-w-0 truncate text-sm text-foreground">
                          {kindLabel[entry.kind]}
                          <span className="text-muted-foreground"> · {languageName(entry.language)}</span>
                        </span>
                      </span>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 pl-[2.375rem] sm:contents">
                        <span className="text-xs tabular-nums text-muted-foreground sm:text-sm sm:text-foreground">
                          {entry.date ? dateFormat.format(entry.date) : copy.noDate}
                        </span>
                        <span>
                          <span className={cn("inline-flex rounded-full px-2 py-0.5 text-xs font-medium", STATUS_CLASS[entry.status])}>
                            {statusLabel[entry.status]}
                          </span>
                        </span>
                      </div>
                    </div>
                    <span className="shrink-0 justify-self-end">
                      {entry.blobName ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-9"
                          aria-label={props.playLabel}
                          title={props.playLabel}
                          disabled={props.playingBlob !== null && !playing}
                          onClick={() => props.onPlay(entry.blobName as string)}
                        >
                          <Icon name={playing ? "loader2" : "play"} className={cn(playing && "animate-spin")} />
                        </Button>
                      ) : (
                        <span className="block size-9" aria-hidden="true" />
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {current.pageCount > 1 ? (
        <nav className="flex items-center justify-between gap-3" aria-label={copy.historyTitle}>
          <span className="text-xs tabular-nums text-muted-foreground">
            {fill(rangeLabel, { start: format(current.start), end: format(current.end), total: format(current.total) })}
          </span>
          <div className="flex items-center gap-1">
            <Button type="button" variant="outline" size="icon" className="size-8" aria-label={copy.previous} disabled={current.page <= 1} onClick={() => setPage(current.page - 1)}>
              <Icon name="chevronLeft" />
            </Button>
            <span className="min-w-12 text-center text-xs tabular-nums text-muted-foreground">
              {current.page} / {current.pageCount}
            </span>
            <Button type="button" variant="outline" size="icon" className="size-8" aria-label={copy.next} disabled={current.page >= current.pageCount} onClick={() => setPage(current.page + 1)}>
              <Icon name="chevronRight" />
            </Button>
          </div>
        </nav>
      ) : null}
    </section>
  );
}
